/**
 * SECONDARY MARKET (Phase 2). Peer trading of already-issued tokens: what they last traded at, the
 * day's move, the book, and what you hold and could sell. UI-first: placing an order is simulated and,
 * when the backend is wired, will settle through the same compliance and escrow spine as any movement.
 */

import { useEffect, useMemo, useState } from 'react';
import { api, type MarketAsset } from '@/lib/api';
import { ErrorState, LoadingCard } from '@/components/states';
import { useResource } from '@/lib/use-resource';
import { Reveal } from '@/components/motion';
import { Sheet } from '@/components/overlay';

const TONE: Record<string, string> = {
  emerald: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  amber: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  lime: 'bg-lime-500/15 text-lime-600 dark:text-lime-400',
  sky: 'bg-sky-500/15 text-sky-600 dark:text-sky-400',
  violet: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  teal: 'bg-teal-500/15 text-teal-600 dark:text-teal-400',
};

export default function Market() {
  const { data, loading, error, reload } = useResource(() => api.market(), []);
  const assets = useMemo(() => data?.assets ?? [], [data]);
  const [trade, setTrade] = useState<{ asset: MarketAsset; side: 'buy' | 'sell' } | null>(null);

  const held = useMemo(() => assets.filter((a) => a.yourUnits > 0), [assets]);

  if (loading) {
    return (
      <div className="space-y-4">
        <LoadingCard lines={2} />
        <LoadingCard lines={4} />
      </div>
    );
  }

  if (error) return <ErrorState body="We could not load the market." onRetry={reload} />;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-page-title text-ink">Secondary market</h1>
        <p className="mt-0.5 text-sm text-ink-muted">Buy and sell fractions of tokenized assets.</p>
      </header>

      {/* YOUR HOLDINGS you can sell. */}
      {held.length > 0 ? (
        <Reveal>
          <section className="rounded-card border border-border bg-surface p-5 shadow-card">
            <h2 className="font-semibold text-ink">Your holdings</h2>
            <ul className="mt-3 space-y-2">
              {held.map((a) => (
                <li key={a.id} className="flex items-center gap-3">
                  <AssetIcon asset={a} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">{a.title}</p>
                    <p className="text-xs text-ink-subtle">{a.yourUnits} tokens</p>
                  </div>
                  <button
                    onClick={() => setTrade({ asset: a, side: 'sell' })}
                    className="rounded-field border border-border px-3 py-1.5 text-sm font-medium text-ink transition hover:bg-surface-sunken"
                  >
                    Sell
                  </button>
                </li>
              ))}
            </ul>
          </section>
        </Reveal>
      ) : null}

      {/* THE MARKET. */}
      <section>
        <h2 className="mb-2 font-semibold text-ink">Market</h2>
        <div className="overflow-hidden rounded-card border border-border bg-surface shadow-card">
          {/* Column headers, desktop only. */}
          <div className="hidden border-b border-border px-4 py-2 text-micro font-medium uppercase tracking-wider text-ink-subtle sm:grid sm:grid-cols-[1fr_auto_auto_auto] sm:gap-4">
            <span>Asset</span>
            <span className="w-24 text-right">Last</span>
            <span className="w-20 text-right">24h</span>
            <span className="w-16 text-right">Trade</span>
          </div>
          <ul className="divide-y divide-border">
            {assets.map((a) => {
              const up = a.change24h >= 0;
              return (
                <li
                  key={a.id}
                  className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 px-4 py-3 sm:grid-cols-[1fr_auto_auto_auto]"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <AssetIcon asset={a} />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-ink">{a.title}</p>
                      <p className="truncate text-xs text-ink-subtle">
                        {a.category} &middot; Vol {a.volume24h}
                      </p>
                    </div>
                  </div>

                  <div className="w-24 text-right tabular text-sm font-semibold text-ink">
                    KES {a.lastPrice}
                  </div>

                  <div
                    className={`w-20 text-right tabular text-sm font-medium ${up ? 'text-accent' : 'text-danger'}`}
                  >
                    {up ? '+' : ''}
                    {a.change24h}%
                  </div>

                  <div className="col-start-2 row-start-1 flex justify-end sm:col-start-4 sm:row-start-1 sm:w-16">
                    <button
                      onClick={() => setTrade({ asset: a, side: 'buy' })}
                      className="rounded-field bg-primary px-3 py-1.5 text-sm font-medium text-white transition hover:bg-primary-light"
                    >
                      Trade
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <TradeSheet trade={trade} onClose={() => setTrade(null)} />
    </div>
  );
}

function AssetIcon({ asset }: { asset: MarketAsset }) {
  return (
    <span
      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-micro font-bold ${TONE[asset.tone] ?? 'bg-surface-sunken text-ink-muted'}`}
    >
      {asset.title.slice(0, 2).toUpperCase()}
    </span>
  );
}

function TradeSheet({
  trade,
  onClose,
}: {
  trade: { asset: MarketAsset; side: 'buy' | 'sell' } | null;
  onClose: () => void;
}) {
  const [side, setSide] = useState<'buy' | 'sell'>('buy');
  const [units, setUnits] = useState('');
  const [placed, setPlaced] = useState(false);

  useEffect(() => {
    if (trade) {
      setSide(trade.side);
      setUnits('');
      setPlaced(false);
    }
  }, [trade]);

  const asset = trade?.asset;
  const price = asset ? Number((side === 'buy' ? asset.ask : asset.bid).replace(/,/g, '')) : 0;
  const n = Number(units) || 0;
  const total = (price * n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  return (
    <Sheet open={!!trade} onClose={onClose} title={asset ? `Trade ${asset.title}` : 'Trade'}>
      {asset ? (
        placed ? (
          <div className="py-4 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent/15 text-accent">
              <svg
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="m5 13 4 4L19 7"
                />
              </svg>
            </span>
            <p className="mt-3 text-sm text-ink-muted">
              Order placed. It settles through escrow and the compliance checks, and re-confirms the
              price at that moment. (Simulated in this build.)
            </p>
            <button
              onClick={onClose}
              className="mt-4 w-full rounded-field bg-primary py-2.5 text-sm font-medium text-white transition hover:bg-primary-light"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Buy / Sell toggle. */}
            <div className="grid grid-cols-2 gap-1 rounded-pill bg-canvas p-1">
              {(['buy', 'sell'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setSide(s)}
                  disabled={s === 'sell' && asset.yourUnits === 0}
                  className={`rounded-pill py-1.5 text-sm font-medium capitalize transition disabled:opacity-40 ${
                    side === s ? 'bg-surface text-ink shadow-sm' : 'text-ink-muted hover:text-ink'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>

            <dl className="flex justify-between rounded-field bg-surface-sunken px-3 py-2 text-sm">
              <dt className="text-ink-muted">{side === 'buy' ? 'Ask' : 'Bid'} price</dt>
              <dd className="tabular font-medium text-ink">
                KES {side === 'buy' ? asset.ask : asset.bid}
              </dd>
            </dl>

            <div>
              <label htmlFor="units" className="block text-sm font-medium text-ink">
                Tokens {side === 'sell' ? `(you hold ${asset.yourUnits})` : ''}
              </label>
              <input
                id="units"
                inputMode="numeric"
                value={units}
                onChange={(e) => setUnits(e.target.value.replace(/\D/g, ''))}
                placeholder="0"
                className="mt-1 w-full rounded-field border border-border bg-surface px-3 py-2.5 font-mono text-base text-ink outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/25"
              />
            </div>

            <dl className="flex justify-between text-sm">
              <dt className="text-ink-muted">Estimated {side === 'buy' ? 'cost' : 'proceeds'}</dt>
              <dd className="tabular font-semibold text-ink">KES {total}</dd>
            </dl>

            <button
              onClick={() => n > 0 && setPlaced(true)}
              disabled={n <= 0 || (side === 'sell' && n > asset.yourUnits)}
              className="w-full rounded-field bg-primary py-3 text-sm font-semibold text-white shadow-soft transition hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
            >
              {side === 'buy' ? 'Place buy order' : 'Place sell order'}
            </button>
            <p className="text-center text-xs text-ink-subtle">
              A market order settles through escrow and re-confirms price at execution.
            </p>
          </div>
        )
      ) : null}
    </Sheet>
  );
}
